import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, CalendarDays, CheckCircle2, Clock, Eye, Inbox, Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DefautsPanel, EtatScanButton, defautsPayload } from "@/components/scans/DefautsEditor";
import { StatutBadge, defautTexte, fmtDateHeure, nomChauffeur, useScansData } from "@/components/scans/common";
import { api, errMsg } from "@/lib/api";
import { scansSubnav } from "@/lib/subnavs";

function aujourdhui() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function ReceptionDialog({ attribution, scan, categories, onClose, onDone }) {
  const [statut, setStatut] = useState("receptionnee");
  const [defauts, setDefauts] = useState([]);
  const [draft, setDraft] = useState([]);
  const [step, setStep] = useState("form");
  const [commentaire, setCommentaire] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!attribution) return;
    setStatut("receptionnee");
    setDefauts(scan?.defauts || attribution.defauts_attribution || []);
    setStep("form");
    setCommentaire("");
  }, [attribution, scan]);

  if (!attribution) return null;

  const valider = async () => {
    setBusy(true);
    try {
      await api.post(`/scans/attributions/${attribution.id}/reception`, { statut, defauts: defautsPayload(defauts), commentaire });
      toast.success(statut === "receptionnee"
        ? `Scan n° ${attribution.numero} réceptionnée — de nouveau disponible`
        : `Scan n° ${attribution.numero} en attente de constatation`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const StatutBtn = ({ value, icon: Icon, label, hint, tone }) => (
    <button
      type="button"
      onClick={() => setStatut(value)}
      className={`flex-1 text-left rounded-lg border p-3 transition-colors ${statut === value ? tone : "border-border hover:bg-accent"}`}
      data-testid={`reception-statut-${value}`}
    >
      <span className="flex items-center gap-2 text-sm font-semibold"><Icon size={15} /> {label}</span>
      <span className="block text-[11px] text-muted-foreground mt-0.5">{hint}</span>
    </button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[94dvh] overflow-y-auto" data-testid="reception-dialog">
        <DialogHeader>
          <DialogTitle>{step === "etat" ? "État de la scan au retour" : `Réception — scan n° ${attribution.numero}`}</DialogTitle>
          {step === "form" && (
            <DialogDescription>
              {attribution.appareil ? `Matériel n° ${attribution.appareil.numero_serie}. ` : ""}Remise à {nomChauffeur(attribution.chauffeur)}{attribution.tournee ? ` (tournée ${attribution.tournee})` : ""} le {fmtDateHeure(attribution.signe_le)}.
            </DialogDescription>
          )}
        </DialogHeader>

        {step === "form" ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Statut</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <StatutBtn value="receptionnee" icon={CheckCircle2} label="Réceptionnée" hint="Scan rendue : la remise est clôturée" tone="border-emerald-500 bg-emerald-500/10" />
                <StatutBtn value="attente_constatation" icon={AlertTriangle} label="En attente de constatation" hint="Problème à vérifier : la scan reste bloquée" tone="border-red-500 bg-red-500/10" />
              </div>
            </div>
            <EtatScanButton defauts={defauts} onClick={() => { setDraft(defauts); setStep("etat"); }} testId="reception-etat" />
            <div className="space-y-1.5">
              <Label htmlFor="rec-com">Commentaire <span className="text-muted-foreground font-normal">(facultatif)</span></Label>
              <Textarea id="rec-com" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
              <Button type="button" onClick={valider} disabled={busy} data-testid="reception-valider">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />} Valider la réception
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <DefautsPanel list={draft} setList={setDraft} categories={categories} />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setStep("form")}><ArrowLeft size={14} /> Retour</Button>
              <Button type="button" onClick={() => { setDefauts(draft); setStep("form"); }} data-testid="etat-valider">Valider l'état</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SignatureDialog({ attributionId, onClose }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    if (!attributionId) return;
    setA(null);
    api.get(`/scans/attributions/${attributionId}`).then(({ data }) => setA(data)).catch((e) => toast.error(errMsg(e)));
  }, [attributionId]);
  return (
    <Dialog open={!!attributionId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Signature — scan n° {a?.numero}</DialogTitle>
          {a && <DialogDescription>{nomChauffeur(a.chauffeur)} · signée le {fmtDateHeure(a.signe_le)} · remise par {a.signe_par?.nom} ({a.signe_par?.matricule})</DialogDescription>}
        </DialogHeader>
        {a?.signature ? <img src={a.signature} alt="Signature du chauffeur" className="w-full rounded-lg border border-border bg-white" /> : <p className="text-sm text-muted-foreground">Chargement…</p>}
        {a?.defauts_attribution?.length > 0 && (
          <div className="text-xs space-y-0.5">
            <p className="text-muted-foreground">État signé à la remise :</p>
            {a.defauts_attribution.map((d) => <p key={d.id} className="text-red-600 dark:text-red-400">{defautTexte(d)}</p>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ReceptionScanPage() {
  const { scans, categories, reloadScans } = useScansData();
  const [jour, setJour] = useState(aujourdhui());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reception, setReception] = useState(null);
  const [sigId, setSigId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scans/attributions", { params: { jour } });
      setItems(data.attributions);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [jour]);
  useEffect(() => { load(); }, [load]);

  const scanById = useMemo(() => Object.fromEntries(scans.map((s) => [s.id, s])), [scans]);
  const estAujourdhui = jour === aujourdhui();
  const ouvertes = items.filter((a) => a.statut !== "receptionnee").length;

  return (
    <AppShell title="Scans à réceptionner" subnav={scansSubnav()}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Scans à réceptionner</h1>
          <p className="text-sm text-muted-foreground">
            {items.length} remise(s) · {ouvertes} à réceptionner{estAujourdhui ? " (y compris les jours précédents non clôturés)" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-muted-foreground" />
          <Input type="date" value={jour} max={aujourdhui()} onChange={(e) => e.target.value && setJour(e.target.value)} className="w-44" data-testid="reception-date" />
          {!estAujourdhui && <Button variant="outline" size="sm" onClick={() => setJour(aujourdhui())}>Aujourd'hui</Button>}
        </div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <Inbox className="mx-auto mb-2 opacity-60" /> Aucune scan remise {estAujourdhui ? "aujourd'hui" : "ce jour-là"}.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="reception-list">
          {items.map((a) => {
            const r = a.reception;
            const close = a.statut === "receptionnee";
            return (
              <div key={a.id} className="p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`reception-row-${a.numero}`}>
                <div className="flex items-center gap-3 lg:w-40 shrink-0">
                  <span className="font-mono text-xl font-bold leading-tight">{a.numero}{a.appareil && <span className="block text-xs font-normal text-muted-foreground">n° {a.appareil.numero_serie}</span>}</span>
                  <StatutBadge statut={a.statut} label={a.statut_label} />
                </div>
                <div className="flex-1 min-w-0 text-sm">
                  <p className="font-medium">{nomChauffeur(a.chauffeur)}{a.tournee && <span className="text-muted-foreground font-normal"> · tournée {a.tournee}</span>}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} /> Attribuée le {fmtDateHeure(a.signe_le)} par {a.signe_par?.nom || "—"}</p>
                  {r && (
                    <p className={`text-xs ${close ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                      {close ? "Réceptionnée" : "Constat demandé"} le {fmtDateHeure(r.date)} par {r.par?.nom} (matricule {r.par?.matricule}){r.commentaire ? ` — ${r.commentaire}` : ""}
                    </p>
                  )}
                  {(r?.defauts || a.defauts_attribution)?.length > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-0.5">
                      <AlertTriangle size={11} /> {(r?.defauts || a.defauts_attribution).map(defautTexte).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {a.has_signature && <Button size="sm" variant="ghost" onClick={() => setSigId(a.id)} title="Voir la signature"><Eye size={14} /></Button>}
                  {!close && (
                    <Button size="sm" onClick={() => setReception(a)} data-testid={`btn-receptionner-${a.numero}`}>
                      <PackageCheck size={14} /> Réceptionner
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ReceptionDialog
        attribution={reception}
        scan={reception ? scanById[reception.scan_id] : null}
        categories={categories}
        onClose={() => setReception(null)}
        onDone={() => { setReception(null); load(); reloadScans(); }}
      />
      <SignatureDialog attributionId={sigId} onClose={() => setSigId(null)} />
    </AppShell>
  );
}
